import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { NotificationService } from '../../../../../services/notifications.service';
import { BaseService } from '../../../../../services/base.service';
import { IntegrationService } from '../../../../../services/integration.service';
import { FormControl, FormGroup } from '@angular/forms';
import { IpAddress, SettingsForm } from './company-settings.interface';
import { SettingsService } from '../../../../../services/settings.service';
import { Router } from '@angular/router';
import { CompanyService } from '../../../../../services/company.service';
import { Tab } from '../../../../../components/tabs-v2/interfaces/tab.interface';
import { CompanySettingsTab } from '../../../../../components/tabs-v2/enums/tabs.enum';
import { BoardService } from '../../../../../services/board.service';
import { buildUsersMap } from '../../../../../helpers';
import { SelectType } from '../../../../../components/select/select.interface';
import { Clipboard } from '@angular/cdk/clipboard';
import { DialogService } from '../../../../../services/dialog.service';
import { activeTranslations, NAME_FORMAT } from '../../../../../enum/constants';

const API_KEY_MASK_SYMBOL = '*';

@Component({
  selector: 'app-company-settings',
  templateUrl: './company-settings.component.html',
  styleUrls: ['./company-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CompanySettingsComponent implements OnInit, OnDestroy {
  // Destroy relative
  private readonly destroy$: Subject<null> = new Subject<null>();
  public settingsForm!: FormGroup<SettingsForm>;
  public formSubmitted: boolean = false;
  public timeZones: any[] = [];
  public currentZone: any[] = [];
  public Settings: any;
  public apiKey: string = '';
  public apiKeyRegenerating: boolean = false;
  public colors: string[] = [
    '#ea5728',
    '#fa8231',
    '#4b7bec',
    '#483CE4',
    '#fc5c65',
    '#eb3b5a',
    '#fed330',
    '#f7b731',
    '#26de81',
    '#20bf6b',
    '#2bcbba',
    '#0fb9b1',
    '#45aaf2',
    '#2d98da',
    '#a55eea',
    '#8854d0',
    '#000000',
    '#d1d8e0',
  ];

  // Tabs
  public tabs: Tab<CompanySettingsTab>[] = [
    { name: 'general', value: CompanySettingsTab.GENERAL, default: true },
    { name: 'security', value: CompanySettingsTab.SECURITY },
  ];
  protected selectedTab!: Tab<CompanySettingsTab>;

  // Subdomain
  private originalSubdomain: string = '';

  // IP addresses
  public ipAddresses: IpAddress[] = [];
  public newIp: string = '';
  public newIpDescription: string = '';
  public newIpManagerSelection: Array<string | number> = [];
  public usersLoading: boolean = false;
  protected readonly nameFormatOptions = [
    { value: NAME_FORMAT.FIRST_LAST, label: 'name_format_first_last' },
    { value: NAME_FORMAT.LAST_FIRST, label: 'name_format_last_first' },
  ];
  public userOptions: { id: string | number; label: string; avatarUrl?: string }[] = [];

  constructor(
    private readonly notificationService: NotificationService,
    private readonly integrationService: IntegrationService,
    private readonly baseService: BaseService,
    public readonly companyService: CompanyService,
    public readonly settingsService: SettingsService,
    public readonly router: Router,
    private readonly boardService: BoardService,
    private readonly clipboard: Clipboard,
    private readonly dialogService: DialogService,
  ) {}

  ngOnInit() {
    this.getZone();
    this.initForm();
    this.loadIpAddresses();
    this.loadUsers();
    // Initialize selected tab
    this.selectedTab = this.tabs.find((tab) => tab.default) || this.tabs[0];
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public isEditSubdomain(): boolean {
    const createdAt = new Date(this.baseService.currentCompanyInfo.created_at);
    const timeDifference = new Date().getTime() - createdAt.getTime();
    const threeDaysInMs = 3 * 24 * 60 * 60 * 1000;
    return timeDifference < threeDaysInMs || this.baseService.user.is_admin;
  }

  initForm() {
    this.originalSubdomain = this.baseService.currentCompanyInfo.subdomain;
    this.apiKey = this.baseService.currentCompanyInfo.api_key || '';
    this.settingsForm = new FormGroup<SettingsForm>({
      name: new FormControl<string>(this.baseService.currentCompanyInfo.name, { nonNullable: true }),
      subdomain: new FormControl<string>(this.baseService.currentCompanyInfo.subdomain, { nonNullable: true }),
      time_zone: new FormControl(this.baseService.currentCompanyInfo.settings.time_zone),
      primary_color: new FormControl(this.baseService.currentCompanyInfo.settings.primary_color),
      is_only_ip: new FormControl(this.baseService.currentCompanyInfo.settings.is_only_ip || false),
      is_chat_unpin: new FormControl<boolean>(this.baseService.currentCompanyInfo.settings.is_chat_unpin, {
        nonNullable: true,
      }),
      // Enabled by default on the backend
      is_chat_manager_assignment: new FormControl<boolean>(
        this.baseService.currentCompanyInfo.settings.is_chat_manager_assignment ?? true,
        { nonNullable: true },
      ),
      name_format: new FormControl<NAME_FORMAT>(
        this.baseService.currentCompanyInfo.settings.name_format || NAME_FORMAT.FIRST_LAST,
        { nonNullable: true },
      ),
    });
  }

  public get maskedApiKey(): string {
    return this.apiKey ? this.apiKey.replace(/./g, API_KEY_MASK_SYMBOL) : '-';
  }

  public copyApiKey(): void {
    if (!this.apiKey) return;

    if (this.clipboard.copy(this.apiKey)) {
      this.notificationService.showInfo(
        '',
        activeTranslations.notifications_texts.info.copied,
      );
    }
  }

  public regenerateApiKey(): void {
    if (this.apiKeyRegenerating) return;

    this.dialogService
      .openConfirm({
        title: 'regenerate_api_key',
        text: 'regenerate_api_key_confirm',
        confirmText: 'regenerate',
        cancelText: 'cancel',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (confirmed !== true) return;

        this.apiKeyRegenerating = true;
        this.companyService
          .regenerateApiKey()
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              this.apiKey = res.api_key;
              this.updateCurrentCompanyApiKey(res.api_key);
              this.notificationService.showSuccess(
                '',
                activeTranslations.notifications_texts.success.successfully_updated,
              );
            },
            error: () => {
              this.apiKeyRegenerating = false;
            },
            complete: () => {
              this.apiKeyRegenerating = false;
            },
          });
      });
  }

  private updateCurrentCompanyApiKey(apiKey: string): void {
    const currentCompanyInfo = {
      ...this.baseService.currentCompanyInfo,
      api_key: apiKey,
    };

    this.baseService.currentCompanyInfo = currentCompanyInfo;
    this.baseService.company = currentCompanyInfo;
    this.baseService.currentCompanyInfoSubject$.next(currentCompanyInfo);
    localStorage.setItem('companyobj', JSON.stringify(currentCompanyInfo));
  }

  public saveChanges() {
    const formValues = this.settingsForm.getRawValue();
    const settingsPayload: any = {
      time_zone: formValues.time_zone,
      primary_color: formValues.primary_color,
      is_only_ip: formValues.is_only_ip,
      is_chat_unpin: formValues.is_chat_unpin,
      is_chat_manager_assignment: formValues.is_chat_manager_assignment,
      name_format: formValues.name_format,
    };
    const companyPayload = {
      name: formValues.name,
      subdomain: formValues.subdomain,
    };

    forkJoin([
      this.companyService.updateSettings(this.baseService.currentCompanyInfo.settings.id, settingsPayload),
      this.companyService.updateCompany(companyPayload),
    ]).subscribe(() => {
      const newSubdomain = companyPayload.subdomain;
      if (this.isEditSubdomain() && newSubdomain && newSubdomain !== this.originalSubdomain) {
        window.location.href = window.location.href.replace(this.originalSubdomain, newSubdomain);
      } else {
        window.location.reload();
      }
    });
  }

  public saveChangesIps() {
    const formValues = this.settingsForm.getRawValue();
    const payload = {
      is_only_ip: formValues.is_only_ip,
      ips: this.ipAddresses.map((ip) => ({
        ip: ip.ip,
        desc: ip.description || '',
        manager_ids: ip.manager_ids || [],
      })),
    };

    this.companyService.updateSettings(this.baseService.currentCompanyInfo.settings.id, payload).subscribe();
  }

  getZone() {
    this.companyService
      .getZone()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.timeZones = res;
      });
  }

  onChangeZone(e: any) {
    this.currentZone = e.id;
  }

  // Load existing IP addresses
  private loadIpAddresses(): void {
    if (this.baseService.currentCompanyInfo.settings.ips) {
      this.ipAddresses = this.baseService.currentCompanyInfo.settings.ips.map((item: any) => {
        // Handle both old format (manager_id) and new format (manager_ids)
        let managerIds: Array<string | number> = [];
        if (item.manager_ids && Array.isArray(item.manager_ids)) {
          managerIds = item.manager_ids.map((id: any) => String(id));
        } else if (item.manager_id != null) {
          managerIds = [String(item.manager_id)];
        } else if (item.manager?.id != null) {
          managerIds = [String(item.manager.id)];
        }

        return {
          ip: item.ip,
          description: item.desc || item.description,
          manager_ids: managerIds,
          manager_selection: [...managerIds],
        };
      });
    }
  }

  private loadUsers(): void {
    this.usersLoading = true;
    this.boardService
      .getManagers({ take: 500, page: 1 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const usersMap = buildUsersMap(res);
          this.userOptions = Object.values(usersMap || {}).map((user) => ({
            id: user.id,
            label: user.name,
            avatarUrl: user.avatar,
          }));
          const userIds = new Set(this.userOptions.map((option) => String(option.id)));

          // Filter out invalid manager IDs from existing IPs
          this.ipAddresses = this.ipAddresses.map((ip) => {
            const validManagerIds = ip.manager_ids.filter((id) => userIds.has(String(id)));
            return {
              ...ip,
              manager_ids: validManagerIds,
              manager_selection: [...validManagerIds],
            };
          });

          // Filter out invalid manager IDs from new IP form
          this.newIpManagerSelection = this.newIpManagerSelection.filter((id) => userIds.has(String(id)));
        },
        error: () => {
          this.userOptions = [];
          this.usersLoading = false;
        },
        complete: () => {
          this.usersLoading = false;
        },
      });
  }

  // Tab management
  protected onChangeTab(tab: Tab<CompanySettingsTab>): void {
    this.selectedTab = tab;
  }

  // IP management
  public addIpAddress(): void {
    if (this.newIp.trim()) {
      this.ipAddresses.push({
        ip: this.newIp.trim(),
        description: this.newIpDescription.trim() || undefined,
        manager_ids: [...this.newIpManagerSelection],
        manager_selection: [...this.newIpManagerSelection],
      });
      this.newIp = '';
      this.newIpDescription = '';
      this.newIpManagerSelection = [];
    }
  }

  public removeIpAddress(index: number): void {
    this.ipAddresses.splice(index, 1);
  }

  public trackByIp(_index: number, item: IpAddress): string {
    return item.ip;
  }

  public onManagerSelectionChange(ip: IpAddress, value: Array<string | number>) {
    ip.manager_selection = value;
    ip.manager_ids = [...value];
  }

  protected readonly SelectType = SelectType;
  protected readonly CompanySettingsTab = CompanySettingsTab;
}
